import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { BiTrash, BiPlus, BiFilterAlt } from "react-icons/bi";
import { API_BASE_URL } from "../utils/urls.js";
import axiosClient from '../utils/apiClient.js';

const ClientCaseList = ({ is_pi = false }) => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({ status: "", serviceType: "" });
  const pageSize = 10;
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user"));
  const roleName = user?.roleName;
  const isFieldAgent = roleName === "field_agent";
  const canManageCases = roleName === "admin" || roleName === "case_manager";

  const formatLabel = (value) => {
    if (!value) return "";
    return value
      .toString()
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getStatusColor = (value) => {
    const v = (value || "").toLowerCase();

    if (["completed", "served", "closed"].includes(v)) return "bg-green-100 text-green-700";
    if (["in progress", "attempted"].includes(v)) return "bg-blue-100 text-blue-700";
    if (["pending", "assigned"].includes(v)) return "bg-yellow-100 text-yellow-700";
    if (["failed", "cancelled", "returned"].includes(v)) return "bg-red-100 text-red-700";

    return "bg-gray-100 text-gray-700";
  };

  const fetchCases = async (page = 1) => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("Authentication token not found.");
      setLoading(false);
      return;
    }

    setLoading(true);
    const queryParams = {
      page,
      pageSize,
      search: searchQuery,
      status: filters.status,
      serviceType: filters.serviceType,
      is_pi: is_pi
    };

    console.log("Query Params:", queryParams);
    try {
      const response = await axiosClient.get(`/client/cases`, { params: queryParams });
      console.log("Cases", response.data.data)
      setCases(response.data.data);
      setTotalPages(response.data.metadata.totalPages);
      setCurrentPage(response.data.metadata.currentPage);
    } catch (err) {
      setError("Failed to load cases. Please try again.");
      console.error("Error fetching cases:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => fetchCases(1), 300);
    return () => clearTimeout(handler);
  }, [searchQuery, filters]);

  useEffect(() => {
    fetchCases(currentPage);
  }, [currentPage]);

  const handlePageChange = (n) => {
    if (n > 0 && n <= totalPages) setCurrentPage(n);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete case "${name}"?`)) return;
    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API_BASE_URL}/client/cases/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchCases(currentPage);
    } catch (err) {
      console.error("Error deleting case:", err);
      setError(err.response?.data?.message || "Failed to delete case.");
    }
  };

  return (
    <div className="p-1 sm:p-4 lg:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-6 gap-3">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Process Server Cases</h1>
        <div className="flex flex-col sm:flex-row w-full sm:w-auto gap-2 sm:gap-4">
          <input
            type="text"
            placeholder="Search by case, client..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="px-4 py-2 border rounded-lg shadow-sm w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {roleName == "client" && (
            <Link
              to="/client/cases/new"
              className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition tour-create-case-btn"
            >
              <BiPlus className="mr-2" /> Create New Case
            </Link>
          )}
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-center">Loading cases…</div>
      ) : error ? (
        <div className="p-6 text-center text-red-500">{error}</div>
      ) : (
        <>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 mb-4 p-4 bg-gray-100 rounded-lg">
        <div className="flex items-center text-gray-600">
          <BiFilterAlt className="mr-2" size={22} /> Filters
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <label className="text-sm font-medium text-gray-700">Status:</label>
            <select
              name="status"
              value={filters.status}
              onChange={(e) =>
                setFilters((p) => ({ ...p, status: e.target.value }))
              }
              className="block w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              <option value="">All</option>
              <option value="Open">Open</option>
              <option value="In Progress">In Progress</option>
              <option value="Closed">Closed</option>
            </select>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <label className="text-sm font-medium text-gray-700">
              Service Type:
            </label>
            <select
              name="serviceType"
              value={filters.serviceType}
              onChange={(e) =>
                setFilters((p) => ({ ...p, serviceType: e.target.value }))
              }
              className="block w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              <option value="">All</option>
              <option value="Standard">Standard</option>
              <option value="Rush">Rush</option>
              <option value="Same-Day">Same-Day</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table for large screens */}
      <div className="hidden sm:block bg-white rounded-lg shadow-sm overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {[
                "Case",
                "Status",
                "Service Status",
                "Client & Assigned",
                "Actions",
              ].map((h) => (
                <th
                  key={h}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-200">
            {cases.length ? (
              cases.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50 transition-colors duration-200">

                  {/* Case Info */}
                  <td className="px-6 py-4 text-sm">
                    <div className="font-semibold text-gray-900">{c.case_name}</div>
                    <div className="text-xs text-gray-500">
                      #{c.case_number} • {c.service_type || "Service"}
                    </div>
                  </td>

                  <td className="px-6 py-4 text-sm">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(c.status)}`}>
                      {formatLabel(c.status)}
                    </span>
                  </td>

                  <td className="px-6 py-4 text-sm">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(c.server_work_status)}`}>
                      {formatLabel(c.server_work_status) || "N/A"}
                    </span>
                  </td>

                  {/* Client + Assigned */}
                  <td className="px-6 py-4 text-sm">
                    <div className="font-medium text-gray-800">{c?.client_name?.includes('NA') ? c.agency : c?.client_name || "No Client"}</div>
                    <div className="text-xs text-gray-500">
                      Assigned: {c.assigned_to_names || "Unassigned"}
                    </div>
                  </td>

                  {/* Actions */}
                  <td className="px-6 py-4 text-right text-sm font-medium space-x-6">
                    {c.created_by_id == user.id && (
                    <Link
                      to={isFieldAgent ? `/agent/cases/${c.id}` : `/client/cases/edit/${c.id}`}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      Edit
                    </Link>

                  )}
                                        <Link
                        to={isFieldAgent ? `/agent/cases/${c.id}` : `/cases/${c.id}`}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        View
                      </Link>


                    {c.created_by_id == user.id && (
                      <button
                        onClick={() => handleDelete(c.id, c.case_name)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <BiTrash className="inline-block h-5 w-5" />
                      </button>

                    )}


                  </td>

                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="4"
                  className="px-6 py-4 text-center text-sm text-gray-500"
                >
                  No cases found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>


      {/* Cards for mobile */}
      <div className="sm:hidden space-y-4">
        {cases.length ? (
          cases.map((c) => (
            <div
              key={c.id}
              className="border rounded-lg bg-white shadow-sm p-4 space-y-1"
            >
              <div className="text-lg font-semibold text-gray-900">
                {c.case_name}
              </div>
              <div className="text-sm text-gray-600">
                Case No: {c.case_number}
              </div>
              <div className="text-sm text-gray-600">
                Type: {c.service_type || "N/A"}
              </div>
              <div className="text-sm text-gray-600">Status: {c.status}</div>
              <div className="text-sm text-gray-600">
                Client: {c.client_name || "N/A"}
              </div>
              <div className="text-sm text-gray-600">
                Assigned: {c.assigned_to_names || "Unassigned"}
              </div>
              <div className="flex justify-between pt-2">
                <Link
                  to={
                    isFieldAgent ? `/agent/cases/${c.id}` : `/cases/${c.id}`
                  }
                  className="text-blue-600 hover:text-blue-900 flex items-center text-sm font-medium"
                >
                  View
                </Link>
                {canManageCases && (
                  <button
                    onClick={() => handleDelete(c.id, c.case_name)}
                    className="text-red-600 hover:text-red-900 flex items-center text-sm font-medium"
                  >
                    <BiTrash className="mr-1" /> Delete
                  </button>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center text-sm text-gray-500">No cases found.</div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-3">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded-lg w-full sm:w-auto ${currentPage === 1
              ? "bg-gray-200 text-gray-500"
              : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
          >
            Previous
          </button>
          <span className="text-gray-700 text-sm">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 rounded-lg w-full sm:w-auto ${currentPage === totalPages
              ? "bg-gray-200 text-gray-500"
              : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
          >
            Next
          </button>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default ClientCaseList;
